/* Compact presentation only: the native picker owns price, inventory and cart. */
(() => {
  if (customElements.get('mas-variant-dropdown')) return;
  customElements.define('mas-variant-dropdown', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.form = document.getElementById(this.dataset.form);
      this.picker = document.getElementById(this.dataset.picker);
      this.trigger = this.querySelector('[data-vd-open]');
      this.panel = this.querySelector('[data-vd-panel]');
      this.search = this.querySelector('[data-vd-search]');
      this.error = this.querySelector('[data-vd-error]');
      this.rows = [...this.querySelectorAll('[data-vd-option]')];
      if (!this.form || !this.picker || !this.rows.length) return;
      this.options = new Map(this.rows.map(row => [row.dataset.variantId, row]));
      this.shortenLabels();
      const shrimpColours = this.dataset.variantFamily === 'shrimp' || this.rows.every(row => row.dataset.title.toLowerCase().includes('cherry shrimp - '));
      this.querySelector('[data-vd-label]').textContent = shrimpColours ? 'Choose variety' : 'Select option';
      this.querySelector('[data-vd-hint]').textContent = `Tap to see all ${this.rows.length} ${shrimpColours ? 'colours' : 'options'}`;
      this.search.placeholder = shrimpColours ? 'Search varieties' : `Search ${this.rows.length} options`;
      this.trigger.addEventListener('click', () => this.panel.hidden ? this.open() : this.close(), {signal});
      this.trigger.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault(); this.open();
          const available = this.visibleRows();
          const row = available.find(item => item.dataset.variantId === this.selected) || available[0];
          row?.focus({preventScroll:true});
        }
      }, {signal});
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.panel.hidden) { event.preventDefault(); event.stopPropagation(); this.close(true); }
      }, {signal});
      document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.close(); }, {signal});
      this.addEventListener('focusout', event => { if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close(); }, {signal});
      this.search.addEventListener('input', () => this.filter(), {signal});
      this.search.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown') { event.preventDefault(); this.visibleRows()[0]?.focus(); }
      }, {signal});
      this.rows.forEach(row => {
        row.addEventListener('click', () => this.choose(row), {signal});
        row.addEventListener('keydown', event => {
          if (!['ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
          event.preventDefault();
          const visible = this.visibleRows();
          const index = visible.indexOf(row);
          const next = event.key === 'Home' ? visible[0] : event.key === 'End' ? visible.at(-1) : visible[(index + (event.key === 'ArrowUp' ? -1 : 1) + visible.length) % visible.length];
          next?.focus();
        }, {signal});
      });
      ['change','variant:change'].forEach(type => this.form.addEventListener(type, () => { this.activate(); this.sync(); }, {signal}));
      this.readyObserver = new MutationObserver(() => this.activate());
      this.readyObserver.observe(this.form, {subtree:true,attributes:true,attributeFilter:['disabled']});
      customElements.whenDefined('hdt-variant-picker').then(() => { if (!signal.aborted) this.activate(); });
      // Native variant data is fetched asynchronously without a ready event.
      // The original controls stay usable until it is ready, including on slow connections.
      let attempts = 0;
      const awaitPicker = () => {
        this.activate();
        if (!signal.aborted && !this.hasAttribute('data-ready') && ++attempts < 80) this.readyTimer = setTimeout(awaitPicker, 125);
      };
      awaitPicker();
    }
    shortenLabels() {
      const names = this.rows.map(row => row.dataset.title);
      const words = names[0].split(/\s+/);
      let common = words.length - 1;
      names.slice(1).forEach(name => {
        const tokens = name.split(/\s+/); let i = 0;
        while (i < common && tokens[i] === words[i]) i++;
        common = i;
      });
      const prefix = this.dataset.optionPrefix || words.slice(0, common).join(' ');
      const removable = (this.dataset.optionPrefix || prefix.length >= 12) && names.every(name => name.startsWith(prefix) && name.slice(prefix.length).replace(/^[\s–—\-:/]+/, '').length > 1);
      this.rows.forEach(row => {
        row.shortName = removable ? row.dataset.title.slice(prefix.length).replace(/^[\s–—\-:/]+/, '') : row.dataset.title;
        row.querySelector('[data-vd-option-name]').textContent = row.shortName;
        row.setAttribute('aria-label', `${row.dataset.title} — ${row.dataset.price}${row.disabled ? ' — Sold out' : ''}`);
      });
      const context = this.querySelector('[data-vd-context]');
      if (removable) { context.textContent = this.dataset.optionContext || prefix.replace(/[\s–—\-:/]+$/, ''); context.hidden = false; }
    }
    activate() {
      if (this.hasAttribute('data-ready') || !this.picker.masterInput || typeof this.picker.selectVariantByID !== 'function') return;
      this.setAttribute('data-ready', '');
      this.picker.setAttribute('data-mas-dropdown-active', '');
      this.readyObserver.disconnect(); clearTimeout(this.readyTimer);
      this.sync();
    }
    sync() {
      this.selected = String(this.form.elements.namedItem('id')?.value || '');
      const row = this.options.get(this.selected);
      this.querySelector('[data-vd-selected-name]').textContent = row ? row.shortName + (row.disabled ? ' · Sold out' : '') : 'Choose an option';
      this.querySelector('[data-vd-selected-price]').textContent = row?.dataset.price || '';
      const swatch = row?.querySelector('[data-vd-swatch]');
      const selectedSwatch = this.querySelector('[data-vd-selected-swatch]');
      selectedSwatch.replaceChildren(...(swatch ? [swatch.cloneNode(true)] : []));
      selectedSwatch.hidden = !swatch;
      this.rows.forEach(item => item.setAttribute('aria-selected', String(item === row)));
      this.setTabStop();
    }
    visibleRows() { return this.rows.filter(row => !row.hidden && !row.disabled); }
    setTabStop() {
      const visible = this.visibleRows();
      const focusRow = visible.find(row => row.dataset.variantId === this.selected) || visible[0];
      this.rows.forEach(row => { row.tabIndex = row === focusRow ? 0 : -1; });
    }
    open() {
      if (!this.picker.masterInput || !this.panel.hidden) return;
      this.sync(); this.search.value = ''; this.filter();
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded','true');
      const row = this.options.get(this.selected);
      const scroller = this.querySelector('.mas-vd-scroll');
      if (row) scroller.scrollTop = row.offsetTop - scroller.offsetTop;
    }
    close(returnFocus = false) {
      if (!this.panel || this.panel.hidden) return;
      this.panel.hidden = true; this.trigger.setAttribute('aria-expanded','false'); this.error.hidden = true;
      if (returnFocus) this.trigger.focus({preventScroll:true});
    }
    filter() {
      const words = this.search.value.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
      let count = 0;
      this.rows.forEach(row => { row.hidden = !words.every(word => row.dataset.title.toLocaleLowerCase().includes(word)); if (!row.hidden) count++; });
      this.querySelector('[data-vd-empty]').hidden = count !== 0;
      this.setTabStop();
    }
    choose(row) {
      if (!row || row.disabled || !this.picker.masterInput) return;
      try {
        this.picker.removeAttribute('no-pick');
        this.picker.selectVariantByID(row.dataset.variantId, true);
        if (String(this.form.elements.namedItem('id')?.value) !== row.dataset.variantId) throw new Error('Selection did not update');
        this.sync(); this.close(true);
      } catch (_) {
        this.error.textContent = 'This option could not be selected. Please try again.';
        this.error.hidden = false;
      }
    }
    disconnectedCallback() {
      this.picker?.removeAttribute('data-mas-dropdown-active');
      this.readyObserver?.disconnect(); clearTimeout(this.readyTimer);
      this.controller?.abort(); this.controller = null;
      this.removeAttribute('data-ready');
    }
  });
})();
